import axios from '@/libs/axios'
import {
  checkHiddenAttr,
  designateOptions,
  designateValue,
  transformField,
} from '@/helpers/formConditions'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import Vue from 'vue'

export default {
  namespaced: true,
  state: {
    preRegFormsList: [],
    preRegFormsShortList: [],
    currentTabIndex: 0,
    manageableUserId: null,
    manageableUserName: null,
    isLoading: false,
    prePregUserAttributes: [],
  },
  getters: {
    getPreRegFormsList: state => state.preRegFormsList,
    getCurrentTabIndex: state => state.currentTabIndex,
    getManageableUserId: state => state.manageableUserId,
    getManageableUserName: state => state.manageableUserName,
    getIsLoading: state => state.isLoading,
    getPreRegUserAttributes: state => state.prePregUserAttributes,
    getUnfilledRequiredForms: state => {
      return state.preRegFormsList.filter(form =>
        form.is_required && form.status !== 'Approved'
      )
    },
    getHasUnfilledRequiredForms: state => {
      return state.preRegFormsList.filter(form =>
        form.is_required && form.status !== 'Approved'
      ).length > 0
    },
    getUnfilledForms: state => {
      return state.preRegFormsList.filter(form =>
        form.status !== 'Approved'
      )
    },
    getHasUnfilledForms: state => {
      return state.preRegFormsList.filter(form =>
        form.status !== 'Approved'
      ).length > 0
    },
  },
  mutations: {
    SET_PRE_REG_FORMS_LIST: (state, val) => {
      state.preRegFormsList = val
    },
    SET_CURRENT_TAB_INDEX: (state, val) => {
      state.currentTabIndex = val
    },
    SET_MANAGEABLE_USER_ID: (state, data) => {
      state.manageableUserId = data
    },
    SET_MANAGEABLE_USER_NAME: (state, data) => {
      state.manageableUserName = data
    },
    SET_IS_LOADING: (state, data) => {
      state.isLoading = data
    },
    USER_PRE_REG_ATTRIBUTES: (state, data) => {
      state.prePregUserAttributes = data
    },
    SET_FORMS_DATA: state => {
      let index = 0
      state.preRegFormsList.filter(preRegForm => preRegForm.form).forEach(preRegForm => {
        preRegForm.hasFileField = false
        preRegForm.hasSignatureField = false

        preRegForm.preparedFields = preRegForm.form.fields.map(field => {
          if (field.type === 'file_upload') preRegForm.hasFileField = true
          if (field.type === 'signature') preRegForm.hasSignatureField = true

          const ref = field.name + index
          index++

          const options = designateOptions(field.type, field.options)

          return {
              id: field.id,
              pre_reg_form_id: preRegForm.id,
              ref: ref,
              value: designateValue(field.type, field.default, options),
              label: field.label,
              name: field.name,
              options: options,
              settings: field.settings,
              type: transformField(field.type),
              required: field.required,
              hidden: checkHiddenAttr(field.conditions.filter(item => item.action === 'Show')),
              use_prefill: field.use_prefill,
              attribute_field_name: field.attribute_field_name,
            }
        })
      })
    },
    SET_FORMS_VALUES_FROM_FIELDS_RESULTS: state => {
      state.preRegFormsList.filter(preRegForm => preRegForm.form).forEach(preRegForm => {
        preRegForm.results.forEach(fieldResult => {
          preRegForm.preparedFields.forEach(field => {
            if (field.id === fieldResult.field_id) {
              if (field.type === 'file_upload') {
                field.value = fieldResult.files.map(item => item.path) || []
                field.files = fieldResult.files || []
              } else {
                field.value = designateValue(field.type, fieldResult.result, field.options)
              }
            }
          })
        })
      })
    },
    SET_FORMS_VALUES_FROM_USER_ATTRIBUTES: state => {
      state.prePregUserAttributes.forEach(userAttribute => {
        state.preRegFormsList.filter(preRegForm => preRegForm.form && preRegForm.use_form_prefill !== false).forEach(preRegForm => {
          preRegForm.preparedFields.filter(field => field.use_prefill).forEach(field => {
            if (field.name === userAttribute.key || field.attribute_field_name === userAttribute.key) {
              field.value = designateValue(field.type, userAttribute.value, field.options)
            }
          })
        })
      })
    },
    UPDATE_RELATED_VALUE_IN_PRE_REG_FORM: (state, { preRegFormId, updatedFieldId, value }) => {
      const formIndex = state.preRegFormsList.findIndex(form => form.id === preRegFormId)

      if (formIndex !== -1) {
        const preRegForm = state.preRegFormsList[formIndex]

        const updatedField = preRegForm.preparedFields.find(f => f.id === updatedFieldId)

        if (updatedField) {
          updatedField.value = value

          Vue.set(state.preRegFormsList, formIndex, preRegForm)
        }
      }
    },
  },
  actions: {
    async fetchPreRegFormsList(store, params) {
			try {
				const response = await axios.get('pre-registration-forms', { params })
        store.commit('SET_PRE_REG_FORMS_LIST', response.data.data)

        return response.data.data
			} catch (error) {
				return error
			}
		},
    async fetchUserAttributes(store, params) {
      try {
        const response = await axios.get('user-attributes', { params })
        store.commit('USER_PRE_REG_ATTRIBUTES', response.data.data)

        return response.data.data
      } catch (error) {
        return error
      }
    },
    async savePreRegFormResult(store, queryParams) {
      try {
        const response = await axios.post('pre-registration-forms/submit', queryParams)

        Vue.$toast({
          component: ToastificationContent,
          props: {
            title: 'Pre-registration form submitted successfully',
            icon: 'ThumbsUpIcon',
            variant: 'info',
          },
        })
        return response
      } catch (error) {
        // Server responded with an error status (e.g. 422 validation) - resolve
        // with the response so the caller can keep inspecting status/data like before.
        if (error.response) {
          return error.response
        }

        // Network/timeout failure - there is no response to resolve with. Re-throw
        // so the caller's catch() runs instead of silently resolving with undefined
        // and blowing up later on response.status / response.data.
        throw error
      }
    },
  },
}